/**
 * The connect return leg of social publishing. The connect flow is a full
 * browser navigation: the API sends the user to the platform, and the
 * platform sends them back to our `returnTo` with the outcome in the query
 * string (`socialConnect=success|error`). Pure so it can be unit-tested;
 * `useSocialConnectReturn` consumes it on every page under the nav.
 */

/** The Settings tab that lists the connected accounts. The tab registry and the links to it read it. */
export const PUBLISHING_SETTINGS_TAB = 'publishing';
export const PUBLISHING_SETTINGS_PATH = `/settings/${PUBLISHING_SETTINGS_TAB}`;

const SOCIAL_CONNECT_RESULT_PARAMS = ['socialConnect', 'platform', 'connectionId', 'errorCode'];

export type SocialConnectResult =
  | {
      outcome: 'success';
      platform: string;
      /** One per account the login connected: always one on TikTok, every Page the user shared on Facebook. */
      connectionIds: string[];
    }
  | { outcome: 'error'; platform: string; errorCode: string };

/** Null when the query carries no connect outcome this client understands. */
export function parseSocialConnectResult(params: URLSearchParams): SocialConnectResult | null {
  const outcome = params.get('socialConnect');
  const platform = params.get('platform') || 'unknown';
  if (outcome === 'success') {
    return { outcome, platform, connectionIds: params.getAll('connectionId').filter(Boolean) };
  }
  if (outcome === 'error') {
    return { outcome, platform, errorCode: params.get('errorCode') || 'unknown' };
  }
  return null;
}

/** The same query with the connect outcome removed, so a refresh cannot replay it. */
export function stripSocialConnectResult(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of SOCIAL_CONNECT_RESULT_PARAMS) next.delete(key);
  return next;
}

/** The `errorCode` values the API documents for the connect return. */
export type SocialConnectErrorCode =
  | 'denied'
  | 'not_logged_in'
  | 'invalid_state'
  | 'exchange_failed'
  | 'connect_failed'
  | 'no_accounts'
  | 'platform_unavailable';

/** The user declined at the platform. Nothing went wrong, so it gets no error toast. */
export const SOCIAL_CONNECT_DENIED: SocialConnectErrorCode = 'denied';

const SOCIAL_CONNECT_ERROR_COPY: Record<SocialConnectErrorCode, string> = {
  denied: 'You cancelled the connection.',
  not_logged_in: 'Please sign in and try again.',
  invalid_state: 'The connection attempt expired. Try again.',
  exchange_failed: 'Something went wrong on the way back from the platform. Try again.',
  connect_failed: 'Something went wrong starting the connection. Try again.',
  // The login went through but shared nothing we can post to. Each module words its own reading (`noAccountsNote`); this is the line for a platform without one.
  no_accounts:
    'Your login shared no account that StarZero can post to. Connect again, allowing everything asked for, with an account that can post.',
  platform_unavailable: 'This platform is not available right now.',
};

// Own-property check: `in` would accept 'toString' through the prototype.
const isSocialConnectErrorCode = (code: string): code is SocialConnectErrorCode =>
  Object.prototype.hasOwnProperty.call(SOCIAL_CONNECT_ERROR_COPY, code);

/**
 * User copy for an API `errorCode`; a code newer than this client is worded
 * generically. `noAccountsNote` is the platform module's wording of
 * `no_accounts` (what the login lacked on that platform), null when the
 * platform has no module here.
 */
export const describeSocialConnectError = (
  errorCode: string,
  noAccountsNote: string | null,
): string => {
  if (errorCode === 'no_accounts' && noAccountsNote) return noAccountsNote;
  return isSocialConnectErrorCode(errorCode)
    ? SOCIAL_CONNECT_ERROR_COPY[errorCode]
    : 'Something went wrong. Try again.';
};
